
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { Award, Users, Zap, Shield, CheckCircle, Star } from "lucide-react";
import { Link } from "react-router-dom";

const AboutTrueCore = () => {
  // Complete product structured data with all required fields
  const productSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': 'Custom Android Tablets - 25+ Years Manufacturing Excellence',
      'image': [
        'https://www.truecoregadgets.com/lovable-uploads/db06e8f0-c84f-4067-95e8-1637cc01e1fc.png'
      ],
      'description': 'Premium ruggedized Android tablets manufactured in India with 25+ years of hardware expertise. IP68 waterproof, dustproof, enterprise security.',
      'brand': {
        '@type': 'Brand',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'manufacturer': {
        '@type': 'Organization',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'offers': {
        '@type': 'Offer',
        'price': '45000',
        'priceCurrency': 'INR',
        'priceValidUntil': '2025-12-31',
        'availability': 'https://schema.org/InStock',
        'seller': {
          '@type': 'Organization',
          'name': 'TrueCore Gadgets'
        }
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.8',
        'reviewCount': '127',
        'bestRating': '5',
        'worstRating': '1'
      },
      'review': [
        {
          '@type': 'Review',
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': '5',
            'bestRating': '5'
          },
          'author': {
            '@type': 'Person',
            'name': 'Rajesh Kumar'
          },
          'reviewBody': 'Exceptional build quality and reliability for our field operations.',
          'datePublished': '2024-11-15'
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': 'Custom PCs - 25+ Years Manufacturing Excellence',
      'image': [
        'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop'
      ],
      'description': 'High-performance custom PCs and workstations. Premium components, custom cooling solutions, and professional warranty for business needs.',
      'brand': {
        '@type': 'Brand',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'manufacturer': {
        '@type': 'Organization',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'offers': {
        '@type': 'Offer',
        'price': '65000',
        'priceCurrency': 'INR',
        'priceValidUntil': '2025-12-31',
        'availability': 'https://schema.org/InStock',
        'seller': {
          '@type': 'Organization',
          'name': 'TrueCore Gadgets'
        }
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.9',
        'reviewCount': '87',
        'bestRating': '5',
        'worstRating': '1'
      },
      'review': [
        {
          '@type': 'Review',
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': '5',
            'bestRating': '5'
          },
          'author': {
            '@type': 'Person',
            'name': 'Vikram Mehta'
          },
          'reviewBody': 'Outstanding custom PC builds with excellent performance and reliability.',
          'datePublished': '2024-11-20'
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': 'Desktop Computers - 25+ Years Manufacturing Excellence',
      'image': [
        'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop'
      ],
      'description': 'Enterprise-grade custom desktop computers built for heavy computation, AI/ML, and enterprise environments requiring reliability.',
      'brand': {
        '@type': 'Brand',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'manufacturer': {
        '@type': 'Organization',
        'name': 'TrueCore Gadgets',
        'foundingDate': '2000'
      },
      'offers': {
        '@type': 'Offer',
        'price': '85000',
        'priceCurrency': 'INR',
        'priceValidUntil': '2025-12-31',
        'availability': 'https://schema.org/InStock',
        'seller': {
          '@type': 'Organization',
          'name': 'TrueCore Gadgets'
        }
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.9',
        'reviewCount': '89',
        'bestRating': '5',
        'worstRating': '1'
      },
      'review': [
        {
          '@type': 'Review',
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': '5',
            'bestRating': '5'
          },
          'author': {
            '@type': 'Person',
            'name': 'Priya Singh'
          },
          'reviewBody': 'Reliable enterprise desktop solutions with excellent scalability options.',
          'datePublished': '2024-11-18'
        }
      ]
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>About TrueCore Gadgets – 25 Years of Trusted Innovation in Electronics Manufacturing</title>
        <meta name="description" content="Learn about TrueCore Gadgets' 25-year journey as India's leading custom electronics manufacturer. Discover our commitment to innovation, quality, and customer satisfaction in tablets, PCs, and smart devices." />
        <meta name="keywords" content="TrueCore Gadgets history, electronics manufacturer India, custom gadgets company, 25 years experience, innovation story, manufacturing excellence" />
        
        <link rel="canonical" href="https://www.truecoregadgets.com/about-truecore-gadgets" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About TrueCore Gadgets – 25 Years of Innovation" />
        <meta property="og:description" content="Discover TrueCore Gadgets' journey as India's premier custom electronics manufacturer with 25+ years of experience." />
        <meta property="og:url" content="https://www.truecoregadgets.com/about-truecore-gadgets" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />

        {/* Product structured data */}
        {productSchemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      <div className="container mx-auto py-12 px-4">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">About TrueCore Gadgets</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            25+ years of innovation, excellence, and trust in custom electronics manufacturing
          </p>
        </div>

        {/* Company Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <p className="text-muted-foreground mb-4">
              Founded in 2000, TrueCore Gadgets has been at the forefront of custom electronics manufacturing in India. 
              What started as a small electronics workshop has grown into a trusted name serving enterprises across the country.
            </p>
            <p className="text-muted-foreground mb-4">
              Our journey began with a simple vision: to create high-quality, customized electronic solutions that meet 
              the unique needs of Indian businesses. Over the years, we've expanded our capabilities and refined our 
              processes to become a leader in custom tablets, PCs, and smart devices.
            </p>
            <p className="text-muted-foreground">
              Today, with over 25 years of experience, we continue to innovate and deliver solutions that empower 
              businesses to achieve their goals with technology they can trust.
            </p>
          </div>
          <div className="relative">
            <img 
              src="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" 
              alt="TrueCore Gadgets - 25+ Years Manufacturing Excellence" 
              className="rounded-lg shadow-lg w-full"
            />
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="bg-muted/50 rounded-lg p-8 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 flex items-center">
                <Shield className="h-6 w-6 text-primary mr-2" />
                Our Mission
              </h3>
              <p className="text-muted-foreground">
                To provide innovative, reliable, and customized electronic solutions that empower businesses 
                to enhance their operations and achieve sustainable growth through cutting-edge technology.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 flex items-center">
                <Zap className="h-6 w-6 text-primary mr-2" />
                Our Vision
              </h3>
              <p className="text-muted-foreground">
                To be India's most trusted partner for custom electronics manufacturing, known for our 
                commitment to quality, innovation, and customer satisfaction across all industries.
              </p>
            </div>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <div className="text-center p-6 bg-card rounded-lg shadow-md">
            <div className="text-3xl font-bold text-primary mb-2">25+</div>
            <div className="text-muted-foreground">Years Experience</div>
          </div>
          <div className="text-center p-6 bg-card rounded-lg shadow-md">
            <div className="text-3xl font-bold text-primary mb-2">500+</div>
            <div className="text-muted-foreground">Happy Clients</div>
          </div>
          <div className="text-center p-6 bg-card rounded-lg shadow-md">
            <div className="text-3xl font-bold text-primary mb-2">10,000+</div>
            <div className="text-muted-foreground">Devices Delivered</div>
          </div>
          <div className="text-center p-6 bg-card rounded-lg shadow-md">
            <div className="text-3xl font-bold text-primary mb-2">99.8%</div>
            <div className="text-muted-foreground">Customer Satisfaction</div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Award className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Quality Excellence</h3>
              <p className="text-muted-foreground">
                We maintain the highest standards in every product we manufacture, ensuring reliability and performance.
              </p>
            </div>
            <div className="text-center">
              <Users className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Customer First</h3>
              <p className="text-muted-foreground">
                Our customers' success is our priority. We work closely to understand and meet their unique requirements.
              </p>
            </div>
            <div className="text-center">
              <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Innovation</h3>
              <p className="text-muted-foreground">
                We continuously invest in new technologies and processes to deliver cutting-edge solutions.
              </p>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-primary/10 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Why Choose TrueCore Gadgets?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Proven Track Record</h4>
                <p className="text-sm text-muted-foreground">25+ years of consistent delivery and customer satisfaction</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Custom Solutions</h4>
                <p className="text-sm text-muted-foreground">Tailored electronics designed to meet your specific business needs</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Quality Assurance</h4>
                <p className="text-sm text-muted-foreground">ISO certified manufacturing processes and rigorous testing</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Pan-India Support</h4>
                <p className="text-sm text-muted-foreground">Comprehensive service network across major Indian cities</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Work Together?</h2>
          <p className="text-muted-foreground mb-6">
            Let's discuss how our 25+ years of experience can help bring your custom electronics vision to life.
          </p>
          <div className="space-x-4">
            <Link to="/contact-truecore" className="tech-btn-primary">
              Contact Us Today
            </Link>
            <Link to="/products" className="tech-btn-outline">
              View Our Products
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AboutTrueCore;
